import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaPlane, FaCompass, FaUser, FaSignOutAlt, FaBars, FaTimes, FaCrown, FaRocket, FaBell, FaCheck, FaTrash, FaMapMarkedAlt, FaCreditCard, FaChevronDown, FaChartBar, FaCog, FaComments, FaFlask, FaToolbox } from 'react-icons/fa';
import { FaDocker } from 'react-icons/fa6';
import { getNotifications, markNotificationsRead, deleteNotification } from '../api/client';

// Get WebSocket URL from environment or derive from window location
const getWebSocketUrl = () => {
  // Use explicit WS URL if provided
  if (process.env.REACT_APP_WS_URL) {
    return process.env.REACT_APP_WS_URL;
  }
  // Derive from current page location for relative API URLs
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}`;
};

// Analytics admin emails
const ANALYTICS_ADMIN_EMAILS = ['yatinpatel02@gmail.com', 'chirag1401@gmail.com', 'nimisha0114@yahoo.com', 'chirag1401@yahoo.com'];

const Header = ({ user, currentView, onNavigate, onLogout }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [bellAnimating, setBellAnimating] = useState(false);
  const notificationRef = useRef(null);
  const mobileNotificationRef = useRef(null);
  const userMenuRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectDelay = 5000; // Max 5 seconds between reconnects

  const isPaidTier = !['free', 'explorer', 'promotional'].includes(user?.subscription_tier) && user?.subscription_tier;
  const isAnalyticsAdmin = ANALYTICS_ADMIN_EMAILS.includes(user?.email?.toLowerCase());

  // Fetch notifications on mount
  const fetchNotifications = useCallback(async () => {
    try {
      const data = await getNotifications();
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, []);

  // WebSocket connection for real-time notifications
  const connectWebSocket = useCallback(() => {
    const token = localStorage.getItem('access_token');
    if (!token) return;

    const wsUrl = `${getWebSocketUrl()}/ws/notifications?token=${token}`;

    try {
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log('Notification WebSocket connected');
        reconnectAttemptsRef.current = 0; // Reset reconnect attempts on successful connection
        // Start ping interval to keep connection alive (every 15 seconds for faster detection)
        pingIntervalRef.current = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send('ping');
          }
        }, 15000);
      };

      wsRef.current.onmessage = (event) => {
        if (event.data === 'pong') return; // Ignore pong responses

        try {
          const data = JSON.parse(event.data);
          if (data.type === 'new_notification') {
            // Add new notification to the top of the list
            setNotifications((prev) => [data.data, ...prev]);
            setUnreadCount((prev) => prev + 1);
            // Trigger bell animation
            setBellAnimating(true);
            setTimeout(() => setBellAnimating(false), 1000);
            // Play notification chime sound
            try {
              const audioContext = new (window.AudioContext || window.webkitAudioContext)();
              const oscillator = audioContext.createOscillator();
              const gainNode = audioContext.createGain();
              oscillator.connect(gainNode);
              gainNode.connect(audioContext.destination);
              oscillator.frequency.setValueAtTime(830, audioContext.currentTime); // First note
              oscillator.frequency.setValueAtTime(1050, audioContext.currentTime + 0.1); // Second note
              gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
              gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
              oscillator.start(audioContext.currentTime);
              oscillator.stop(audioContext.currentTime + 0.3);
            } catch (audioErr) {
              console.log('Could not play notification sound');
            }

            // Dispatch custom events for real-time component updates
            if (data.data && data.data.type === 'trip_checkin') {
              // Notify TripTimeline component to refresh
              window.dispatchEvent(new CustomEvent('checkin-notification', { detail: data.data }));
            }
          }
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };

      wsRef.current.onclose = (event) => {
        console.log('Notification WebSocket closed:', event.code);
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }
        // Fast reconnect with exponential backoff (100ms, 200ms, 400ms, ... up to 5s)
        if (event.code !== 4001) {
          const delay = Math.min(100 * Math.pow(2, reconnectAttemptsRef.current), maxReconnectDelay);
          reconnectAttemptsRef.current++;
          reconnectTimeoutRef.current = setTimeout(connectWebSocket, delay);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('Notification WebSocket error:', error);
        // Immediately try to reconnect on error
        if (wsRef.current) {
          wsRef.current.close();
        }
      };
    } catch (e) {
      console.error('Failed to create WebSocket:', e);
    }
  }, []);

  const disconnectWebSocket = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    // Connect WebSocket for real-time notifications
    connectWebSocket();

    // Reconnect immediately when tab becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Check if WebSocket is disconnected and reconnect immediately
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
          reconnectAttemptsRef.current = 0; // Reset for immediate reconnect
          connectWebSocket();
        }
        // Also refresh notifications in case any were missed
        fetchNotifications();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      disconnectWebSocket();
    };
  }, [fetchNotifications, connectWebSocket, disconnectWebSocket]);

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check both desktop and mobile notification dropdowns
      const isInsideDesktopNotification = notificationRef.current && notificationRef.current.contains(event.target);
      const isInsideMobileNotification = mobileNotificationRef.current && mobileNotificationRef.current.contains(event.target);
      if (!isInsideDesktopNotification && !isInsideMobileNotification) {
        setIsNotificationOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMarkAllRead = async () => {
    try {
      setLoadingNotifications(true);
      await markNotificationsRead(null);
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark notifications as read:', error);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const handleDeleteNotification = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await deleteNotification(notificationId);
      setNotifications(notifications.filter(n => n.id !== notificationId));
      const deleted = notifications.find(n => n.id === notificationId);
      if (deleted && !deleted.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const handleNotificationClick = async (notification) => {
    // Debug: Show alert to confirm click is working
    const itineraryId = notification.data?.request_id || notification.data?.itinerary_request_id;
    console.log('Notification clicked:', notification);
    console.log('Itinerary ID:', itineraryId);

    // Close dropdown first
    setIsNotificationOpen(false);

    // Mark as read if unread
    if (!notification.is_read) {
      try {
        await markNotificationsRead([notification.id]);
        setNotifications(prev => prev.map(n =>
          n.id === notification.id ? { ...n, is_read: true } : n
        ));
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
      }
    }

    // Navigate to the itinerary
    if (itineraryId) {
      // Small delay to ensure dropdown closes first
      setTimeout(() => {
        onNavigate('view-itinerary', itineraryId);
      }, 100);
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: FaCompass },
    { id: 'new-itinerary', label: 'Plan Trip', icon: FaPlane },
    { id: 'blog', label: 'Blogs', icon: FaMapMarkedAlt },
    { id: 'tools', label: 'Tools', icon: FaToolbox },
    { id: 'pricing', label: 'Pricing', icon: FaCreditCard },
  ];

  const handleNavigate = (view) => {
    onNavigate(view);
    setIsMobileMenuOpen(false);
    setIsUserMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl shadow-lg border-b border-white/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div
            className="flex items-center cursor-pointer group"
            onClick={() => onNavigate('dashboard')}
          >
            <img
              src="/logo.png"
              alt="ItinerarySeek"
              className="h-[4.5rem] w-auto transition-transform duration-300 group-hover:scale-105"
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = currentView === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => handleNavigate(item.id)}
                  className={`flex items-center space-x-2 px-5 py-2.5 rounded-xl font-semibold transition-all duration-300 ${
                    isActive
                      ? 'bg-gradient-to-r from-primary-500 to-accent-500 text-white shadow-lg shadow-primary-500/30 scale-105'
                      : 'text-neutral-600 hover:text-primary-600 hover:bg-primary-50 hover:shadow-md'
                  }`}
                >
                  <Icon className={`text-lg ${isActive ? 'animate-pulse' : ''}`} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>

          {/* User Section & Logout */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Notification Bell */}
            <div className="relative" ref={notificationRef}>
              <button
                onClick={() => setIsNotificationOpen(!isNotificationOpen)}
                className="relative p-2 rounded-lg text-neutral-700 hover:bg-primary-50 hover:text-primary-600 transition-all"
              >
                <FaBell className={`text-xl ${bellAnimating ? 'animate-bell-ring' : ''}`} />
                {unreadCount > 0 && (
                  <span className={`absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center ${bellAnimating ? 'animate-pulse' : ''}`}>
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Notification Dropdown - Desktop */}
              {isNotificationOpen && (
                <div className="hidden md:block absolute right-0 mt-2 w-80 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-neutral-200 overflow-hidden z-50">
                  <div className="px-4 py-3 bg-gradient-to-r from-primary-500 via-accent-500 to-adventure-500 text-white flex justify-between items-center">
                    <span className="font-bold">Notifications</span>
                    {unreadCount > 0 && (
                      <button
                        onClick={handleMarkAllRead}
                        disabled={loadingNotifications}
                        className="text-xs bg-white/20 hover:bg-white/30 px-2 py-1 rounded flex items-center gap-1 transition-colors"
                      >
                        <FaCheck className="text-xs" />
                        Mark all read
                      </button>
                    )}
                  </div>

                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-8 text-center text-neutral-500">
                        <FaBell className="text-3xl mx-auto mb-2 text-neutral-300" />
                        <p>No notifications yet</p>
                      </div>
                    ) : (
                      notifications.map((notification) => (
                        <div
                          key={notification.id}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            handleNotificationClick(notification);
                          }}
                          className={`px-4 py-3 border-b border-neutral-100 hover:bg-neutral-50 cursor-pointer transition-colors relative ${
                            !notification.is_read ? 'bg-primary-50/50' : ''
                          }`}
                        >
                          <div className="flex items-start gap-3 pointer-events-none">
                            <div className={`mt-1 w-8 h-8 rounded-full flex items-center justify-center ${
                              notification.type === 'itinerary_invite'
                                ? 'bg-green-100 text-green-600'
                                : 'bg-primary-100 text-primary-600'
                            }`}>
                              <FaMapMarkedAlt className="text-sm" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm ${!notification.is_read ? 'font-semibold text-neutral-800' : 'text-neutral-700'}`}>
                                {notification.title}
                              </p>
                              <p className="text-xs text-neutral-500 mt-0.5 line-clamp-2">
                                {notification.message}
                              </p>
                              <p className="text-xs text-neutral-400 mt-1">
                                {formatTimeAgo(notification.created_at)}
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              handleDeleteNotification(notification.id, e);
                            }}
                            className="absolute right-3 top-3 text-neutral-400 hover:text-red-500 p-1 transition-colors z-10"
                          >
                            <FaTrash className="text-xs" />
                          </button>
                          {!notification.is_read && (
                            <div className="absolute left-2 top-1/2 -translate-y-1/2 w-2 h-2 bg-primary-500 rounded-full"></div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* User Menu Dropdown */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="flex items-center space-x-2.5 text-neutral-700 hover:text-primary-600 transition-all duration-300 px-3 py-2 rounded-xl hover:bg-gradient-to-r hover:from-primary-50 hover:to-accent-50 hover:shadow-md"
              >
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-secondary-400 to-coral-500 flex items-center justify-center text-white font-bold shadow-md">
                  {(user?.full_name || user?.email || 'U').charAt(0).toUpperCase()}
                </div>
                <span className="font-semibold text-sm">{user?.full_name || user?.email?.split('@')[0] || 'User'}</span>
                {isPaidTier && (
                  <span className="flex items-center gap-1 bg-gradient-to-r from-golden-400 to-secondary-500 text-white px-2.5 py-1 rounded-full text-xs font-bold shadow-sm">
                    <FaCrown className="text-yellow-200 text-xs" />
                    {(user?.subscription_tier || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                  </span>
                )}
                <FaChevronDown className={`text-xs transition-transform duration-300 ${isUserMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* User Dropdown Menu */}
              {isUserMenuOpen && (
                <div className="absolute right-0 mt-2 w-60 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-neutral-200 overflow-hidden z-50 animate-fade-in">
                  <div className="px-4 py-4 bg-gradient-to-r from-primary-50 via-accent-50 to-secondary-50 border-b border-neutral-200">
                    <p className="font-bold text-neutral-800">{user?.full_name || user?.email?.split('@')[0] || 'User'}</p>
                    <p className="text-xs text-neutral-500 mt-0.5">{user?.email}</p>
                  </div>

                  <div className="py-1">
                    <button
                      onClick={() => handleNavigate('profile')}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-primary-50 transition-colors ${
                        currentView === 'profile' ? 'bg-primary-50 text-primary-600' : 'text-neutral-700'
                      }`}
                    >
                      <FaUser className="text-sm" />
                      <span>Profile</span>
                    </button>
                    {isAnalyticsAdmin && (
                      <button
                        onClick={() => handleNavigate('system')}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-indigo-50 transition-colors ${
                          currentView === 'system' ? 'bg-indigo-50 text-indigo-600' : 'text-indigo-600'
                        }`}
                      >
                        <FaCog className="text-sm" />
                        <span>System</span>
                      </button>
                    )}
                    {isAnalyticsAdmin && (
                      <button
                        onClick={() => handleNavigate('analytics')}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-purple-50 transition-colors ${
                          currentView === 'analytics' ? 'bg-purple-50 text-purple-600' : 'text-purple-600'
                        }`}
                      >
                        <FaChartBar className="text-sm" />
                        <span>Analytics</span>
                      </button>
                    )}
                    {isAnalyticsAdmin && (
                      <button
                        onClick={() => handleNavigate('feedback-review')}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-teal-50 transition-colors ${
                          currentView === 'feedback-review' ? 'bg-teal-50 text-teal-600' : 'text-teal-600'
                        }`}
                      >
                        <FaComments className="text-sm" />
                        <span>Feedback Review</span>
                      </button>
                    )}
                    {isAnalyticsAdmin && (
                      <button
                        onClick={() => handleNavigate('docker-monitoring')}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-cyan-50 transition-colors ${
                          currentView === 'docker-monitoring' ? 'bg-cyan-50 text-cyan-600' : 'text-cyan-600'
                        }`}
                      >
                        <FaDocker className="text-sm" />
                        <span>Docker</span>
                      </button>
                    )}
                    {isAnalyticsAdmin && (
                      <button
                        onClick={() => handleNavigate('test-runner')}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-emerald-50 transition-colors ${
                          currentView === 'test-runner' ? 'bg-emerald-50 text-emerald-600' : 'text-emerald-600'
                        }`}
                      >
                        <FaFlask className="text-sm" />
                        <span>Tests</span>
                      </button>
                    )}
                    <button
                      onClick={() => handleNavigate('account')}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                        currentView === 'account' ? 'bg-purple-50 text-purple-600' : 'hover:bg-purple-50 text-purple-600'
                      }`}
                    >
                      <FaCreditCard className="text-sm" />
                      <span>My Subscription</span>
                    </button>
                    <button
                      onClick={() => handleNavigate('pricing')}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                        currentView === 'pricing' ? 'bg-orange-50 text-orange-600' : 'hover:bg-orange-50 text-orange-600'
                      }`}
                    >
                      <FaRocket className="text-sm" />
                      <span>View Plans</span>
                    </button>
                  </div>

                  <div className="border-t border-neutral-300 py-1">
                    <button
                      onClick={onLogout}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <FaSignOutAlt className="text-sm" />
                      <span>Logout</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Notification & Menu Buttons */}
          <div className="md:hidden flex items-center space-x-2">
            {/* Mobile Notification Bell */}
            <button
              onClick={() => setIsNotificationOpen(!isNotificationOpen)}
              className="relative p-2 rounded-lg text-neutral-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
            >
              <FaBell className={`text-xl ${bellAnimating ? 'animate-bell-ring' : ''}`} />
              {unreadCount > 0 && (
                <span className={`absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center ${bellAnimating ? 'animate-pulse' : ''}`}>
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-lg text-neutral-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
            >
              {isMobileMenuOpen ? <FaTimes className="text-2xl" /> : <FaBars className="text-2xl" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white/95 backdrop-blur-xl border-t border-neutral-200 animate-slide-down">
          <div className="px-4 py-4 space-y-2">
            {/* User Info */}
            <div className="flex items-center justify-between pb-4 border-b border-neutral-200">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-secondary-400 to-coral-500 flex items-center justify-center text-white font-bold text-lg shadow-lg">
                  {(user?.full_name || user?.email || 'U').charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="font-medium text-neutral-800">{user?.full_name || user?.email?.split('@')[0] || 'User'}</p>
                  <p className="text-sm text-neutral-500">{user?.email}</p>
                </div>
              </div>
              {isPaidTier ? (
                <div className="flex items-center gap-1 bg-gradient-to-r from-primary-500 to-secondary-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                  <FaCrown className="text-yellow-300" />
                  <span>{(user?.subscription_tier || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                </div>
              ) : (
                <div className="text-xs bg-neutral-100 text-neutral-600 px-2 py-1 rounded-full">
                  Free Plan
                </div>
              )}
            </div>

            {/* Navigation Items */}
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = currentView === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => handleNavigate(item.id)}
                  className={`w-full flex items-center space-x-3 px-4 py-3.5 rounded-xl font-semibold transition-all duration-300 ${
                    isActive
                      ? 'bg-gradient-to-r from-primary-500 to-accent-500 text-white shadow-lg'
                      : 'text-neutral-700 hover:bg-gradient-to-r hover:from-primary-50 hover:to-accent-50 hover:text-primary-600'
                  }`}
                >
                  <Icon className="text-xl" />
                  <span>{item.label}</span>
                </button>
              );
            })}

            {/* Divider */}
            <div className="border-t border-neutral-300 my-2"></div>

            {/* User Menu Items */}
            <button
              onClick={() => handleNavigate('profile')}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                currentView === 'profile'
                  ? 'bg-gradient-primary text-white shadow-travel'
                  : 'text-neutral-700 hover:bg-primary-50 hover:text-primary-600'
              }`}
            >
              <FaUser className="text-xl" />
              <span>Profile</span>
            </button>
            {/* System Button for Admins */}
            {isAnalyticsAdmin && (
              <button
                onClick={() => handleNavigate('system')}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                  currentView === 'system'
                    ? 'bg-gradient-to-r from-indigo-500 to-indigo-600 text-white shadow-travel'
                    : 'text-indigo-600 hover:bg-indigo-50'
                }`}
              >
                <FaCog className="text-xl" />
                <span>System</span>
              </button>
            )}
            {/* Analytics Button for Admins */}
            {isAnalyticsAdmin && (
              <button
                onClick={() => handleNavigate('analytics')}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                  currentView === 'analytics'
                    ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-travel'
                    : 'text-purple-600 hover:bg-purple-50'
                }`}
              >
                <FaChartBar className="text-xl" />
                <span>Analytics</span>
              </button>
            )}
            {/* Feedback Review Button for Admins */}
            {isAnalyticsAdmin && (
              <button
                onClick={() => handleNavigate('feedback-review')}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                  currentView === 'feedback-review'
                    ? 'bg-gradient-to-r from-teal-500 to-cyan-600 text-white shadow-travel'
                    : 'text-teal-600 hover:bg-teal-50'
                }`}
              >
                <FaComments className="text-xl" />
                <span>Feedback Review</span>
              </button>
            )}
            {/* Docker Monitoring Button for Admins */}
            {isAnalyticsAdmin && (
              <button
                onClick={() => handleNavigate('docker-monitoring')}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                  currentView === 'docker-monitoring'
                    ? 'bg-gradient-to-r from-blue-500 to-cyan-600 text-white shadow-travel'
                    : 'text-cyan-600 hover:bg-cyan-50'
                }`}
              >
                <FaDocker className="text-xl" />
                <span>Docker</span>
              </button>
            )}
            {/* Test Runner Button for Admins */}
            {isAnalyticsAdmin && (
              <button
                onClick={() => handleNavigate('test-runner')}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                  currentView === 'test-runner'
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-travel'
                    : 'text-emerald-600 hover:bg-emerald-50'
                }`}
              >
                <FaFlask className="text-xl" />
                <span>Tests</span>
              </button>
            )}
            {/* My Subscription */}
            <button
              onClick={() => handleNavigate('account')}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                currentView === 'account'
                  ? 'bg-gradient-to-r from-purple-500 to-indigo-600 text-white shadow-travel'
                  : 'text-purple-600 hover:bg-purple-50'
              }`}
            >
              <FaCreditCard className="text-xl" />
              <span>My Subscription</span>
            </button>
            {/* View Plans / Upgrade */}
            <button
              onClick={() => handleNavigate('pricing')}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                currentView === 'pricing'
                  ? 'bg-gradient-to-r from-orange-400 to-pink-500 text-white shadow-travel'
                  : 'text-orange-600 hover:bg-orange-50'
              }`}
            >
              <FaRocket className="text-xl" />
              <span>View Plans</span>
            </button>

            {/* Logout Button */}
            <button
              onClick={onLogout}
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg font-medium text-neutral-700 hover:bg-red-50 hover:text-red-600 transition-all duration-200"
            >
              <FaSignOutAlt className="text-xl" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      )}

      {/* Mobile Notification Dropdown */}
      {isNotificationOpen && (
        <div ref={mobileNotificationRef} className="md:hidden fixed inset-x-4 top-20 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-neutral-200 overflow-hidden z-50 max-h-[70vh] animate-fade-in">
          <div className="px-4 py-3 bg-gradient-to-r from-primary-500 via-accent-500 to-adventure-500 text-white flex justify-between items-center">
            <span className="font-bold">Notifications</span>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  disabled={loadingNotifications}
                  className="text-xs bg-white/20 hover:bg-white/30 px-2 py-1 rounded flex items-center gap-1 transition-colors"
                >
                  <FaCheck className="text-xs" />
                  Mark all read
                </button>
              )}
              <button
                onClick={() => setIsNotificationOpen(false)}
                className="p-1 hover:bg-white/20 rounded transition-colors"
              >
                <FaTimes />
              </button>
            </div>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-neutral-500">
                <FaBell className="text-3xl mx-auto mb-2 text-neutral-300" />
                <p>No notifications yet</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    handleNotificationClick(notification);
                  }}
                  className={`px-4 py-3 border-b border-neutral-100 hover:bg-neutral-50 cursor-pointer transition-colors relative ${
                    !notification.is_read ? 'bg-primary-50/50' : ''
                  }`}
                >
                  <div className="flex items-start gap-3 pointer-events-none">
                    <div className={`mt-1 w-8 h-8 rounded-full flex items-center justify-center ${
                      notification.type === 'itinerary_invite'
                        ? 'bg-green-100 text-green-600'
                        : 'bg-primary-100 text-primary-600'
                    }`}>
                      <FaMapMarkedAlt className="text-sm" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${!notification.is_read ? 'font-semibold text-neutral-800' : 'text-neutral-700'}`}>
                        {notification.title}
                      </p>
                      <p className="text-xs text-neutral-500 mt-0.5 line-clamp-2">
                        {notification.message}
                      </p>
                      <p className="text-xs text-neutral-400 mt-1">
                        {formatTimeAgo(notification.created_at)}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      handleDeleteNotification(notification.id, e);
                    }}
                    className="absolute right-3 top-3 text-neutral-400 hover:text-red-500 p-1 transition-colors z-10"
                  >
                    <FaTrash className="text-xs" />
                  </button>
                  {!notification.is_read && (
                    <div className="absolute left-2 top-1/2 -translate-y-1/2 w-2 h-2 bg-primary-500 rounded-full"></div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
